
/* global cordova:false */

'use strict';

// Wrap the code in a closure in order to hide it from global scope
(function main() {
  // only time reading from the window will be allowed - so we can specify the bootstrap individually
  // across different applications
  const VB_CONF = window.vbInitConfig || {};

  if (VB_CONF.IS_MOBILE) {
    /**
     * Handles the launch of a native mobile app from a deep link url. Has to be defined as early as possible.
     * It is important to notice that this code registers the handleOpenURL but it does not execute anything yet.
     * This is why the content of the function can call requirejs, because at that point when the code is executed
     * (when the handleOpenURL callback is called), requirejs has been configured, this is why we can use the
     * ‘vb/...’ path.
     * @param url deep link url
     */
    window.handleOpenURL = (url) => {
      console.log(`handleOpenURL is invoked with: ${url}`);
      requirejs(['vb/private/mobile/customUrlScheme'], (MobileCustomUrlScheme) => {
        MobileCustomUrlScheme.handleDeepLinkUrl(url);
      });
    };
  }

  const vbRuntime = `visual-runtime${VB_CONF.DEBUG ? '-debug' : ''}.js`;
  // Look up the root location from where this script is loaded which is used to construct the url for
  // loading the vbcs-icon-font.css.
  const scriptSrc = document.currentScript.src;
  const scriptRoot = scriptSrc.substring(0, scriptSrc.indexOf(vbRuntime));
  // start injected shared scripts
  /**
   * Based on the location and vbInitConfig parameters, calculates the application URL. The underlying assumption
   * here is that the worker (for example, sw.js) is registered at the same location as index.html.
   * Depending on the existence of the marker 'vp' in the URL, the application URL can take two values:
   *   1) If the marker is not in the URL, the application URL is the current path.
   *   2) Otherwise the application URL is the part of the URL before the marker.
   *
   * This URL can be different from the base URL used to retrieve the application resource.
   * This is will be used by the router sync navigation state and URL and service worker to cache app resources
   * @param {*} loc Window.location or WorkerGlobalScope.location
   * @param {*} vbInitConfig application configuration
   * @param {*} urlMarker (optional) the urlMarker is defined here and the Configuration class is used to access it
   */
  const calculateApplicationUrl = (loc, vbInitConfig = {}, urlMarker = 'vp') => {
    const appName = vbInitConfig.APP_NAME;

    if (appName) {
      // Match the first occurrence of /appName/ or ending with /appName
      const regex = new RegExp(`/${appName}$|/${appName}/`, 'g');
      const result = regex.exec(loc.pathname);

      if (result) {
        let appUrl = loc.origin + loc.pathname.substring(0, result.index + 1 + appName.length);
        // Make sure to terminate with a '/'
        if (appUrl.slice(-1) !== '/') {
          appUrl += '/';
        }

        return appUrl;
      }
    }
    // Retrieve the URL without search or hash. Depending on context, pathname can be:
    // - /ic/builder/rt/rx/1.0/webApps/abc/ on the main thread
    // - /ic/builder/rt/rx/1.0/webApps/abc/sw.js in the service worker context
    // - /context.html or /debug.html for unit test
    let appUrl = loc.origin + loc.pathname;
    const visualIndex = appUrl.indexOf(`/${urlMarker}/`);

    // Make sure that we either have .../vp/... or it ends with /vp
    if (visualIndex > 0) {
      appUrl = appUrl.substring(0, visualIndex + 1);
    } else if (appUrl.substring(appUrl.length - urlMarker.length - 1) === `/${urlMarker}`) {
      appUrl = appUrl.substring(0, appUrl.length - urlMarker.length - 1);
    } else {
      // Location path might end with a resource
      // eslint-disable-next-line no-restricted-syntax
      for (const r of ['index.html', 'sw.js', 'debug.html', 'context.html']) {
        const resourceIndex = appUrl.indexOf(r);
        if (resourceIndex > 0) {
          appUrl = appUrl.substring(0, resourceIndex);
          break;
        }
      }
    }

    // Make sure to terminate with a '/'
    if (!appUrl.endsWith('/')) {
      appUrl += '/';
    }
    return appUrl;
  };
  /**
   * The base URL is where all the application resources are loaded from.
   * @param {*} vbInitConfig application configuration
   * @param {*} applicationUrl calculated application URL
   * @return {String}  the baseUrl
   */
  const getBaseUrlFromConfig = (vbInitConfig, applicationUrl) => {
    // For mobile, the base URL can only be relative to application URL because
    // application resource are always stored locally
    if (vbInitConfig.IS_MOBILE) {
      return applicationUrl;
    }
    // If BASE_URL is defined in vbInitConfig, we use it as-is.
    let baseUrl = vbInitConfig.BASE_URL;
    // If not defined, the base URL is a combination of application URL and BASE_URL_TOKEN
    if (!baseUrl) {
      baseUrl = applicationUrl;
      let baseUrlToken = vbInitConfig.BASE_URL_TOKEN;
      if (baseUrlToken) {
        // When using vanity URL, BASE_URL_TOKEN starts with '/'
        if (baseUrlToken[0] === '/') {
          baseUrlToken = baseUrlToken.substring(1);
        }
        baseUrl = `${baseUrl}${baseUrlToken}`;
      }
    }
    // Make sure to terminate with a '/', because BASE_URL or BASE_URL_TOKEN might not be
    if (!baseUrl.endsWith('/')) {
      baseUrl += '/';
    }
    return baseUrl;
  };

  // end injected shared scripts
  const configuration = {
    // Hold a copy of this function for testing
    calculateApplicationUrl,

    appName: VB_CONF.APP_NAME,

    // Store the URL marker
    // The urlMarker is defined here and the Configuration class is used to access its value
    urlMarker: 'vp',

    // Calculate the value once
    applicationUrl: calculateApplicationUrl(window.location, VB_CONF),

    getBaseUrlFromConfig,
  };

  /**
   * Build the config object that will be used by requirejs.config
   * This object is built in multiple phase.
   * @param  {Boolean} isMobile true if in mobile environment
   * @return {Object}  a require config object
   */
  function buildRequireConfig(isMobile) {
    const requireConfig = {
      config: {
        text: {
          // allow cross-domain requests
          // remote server allows CORS
          useXhr: () => true,
        },
      },
    };

    // Since we set require baseUrl, this value can later be retrieved by calling requirejs.toUrl('')
    // Note: Don't set the baseUrl when running in action chain test mode since the baseUrl should be one
    // set up by the tests.
    if (VB_CONF.TEST_MODE !== 'actionChain') {
      requireConfig.baseUrl = configuration.getBaseUrlFromConfig(VB_CONF, configuration.applicationUrl);
    }

    requireConfig.map = {
      '*': {
        /* built in types */
        'vb/ServiceDataProvider': 'vb/private/types/serviceDataProvider',
        'vb/ServiceDataProvider2': 'vb/private/types/dataProviders/serviceDataProvider2',
        'vb/MultiServiceDataProvider': 'vb/private/types/multiServiceDataProvider',
        'vb/MultiServiceDataProvider2': 'vb/private/types/dataProviders/multiServiceDataProvider2',
        'vb/ArrayDataProvider': 'vb/private/types/arrayDataProvider',
        'vb/ArrayDataProvider2': 'vb/private/types/arrayDataProvider2',
        'vb/MetadataDescriptor': 'vb/private/types/metadataDescriptor',
        'vb/DefaultSecurityProvider': isMobile
          ? 'vb/private/types/mobileDefaultSecurityProvider' : 'vb/private/types/defaultSecurityProvider',
        'vb/Personalization': 'vb/private/types/personalization',
        'vb/DefaultPersonalizationProvider': 'vb/types/personalizationProvider',
        // For backward compatibility
        'vb/UserSecurityProvider': 'vb/private/types/defaultSecurityProvider',
        'vb/RestHookHandler': 'vb/helpers/restHookHandler',
        'vb/extensions/process/ProcessDataProvider': 'vb/extensions/process/private/types/processDataProvider',
        /* built in transforms */
        // todo: remove ServiceRampTransforms when DT has caught up
        'vb/ServiceRampTransforms': 'vb/private/services/ramp/rampTransforms',
        'vb/BusinessObjectsTransforms': 'vb/private/services/ramp/rampTransforms',

        // dynamic UI
        'vb/FactoryMetadataProviderDescriptor': 'vb/private/types/factoryMetadataProviderDescriptor',
        'vb/extensions/dynamic/ServiceMetadataProviderFactory':
          'vb/extensions/dynamic/private/factories/serviceMetadataProviderFactoryBridge',
        'vb/extensions/dynamic/JsonMetadataProviderFactory':
          'vb/extensions/dynamic/private/factories/jsonMetadataProviderFactoryBridge',
        'vb/extensions/dynamic/DynamicLayoutMetadataProviderFactory':
          'vb/extensions/dynamic/private/factories/dynamicLayoutMetadataProviderFactoryBridge',
        // dynamic UI "metadata" variable types
        'vb/JsonMetadataProviderDescriptor':
          'vb/extensions/dynamic/private/types/jsonMetadataProviderDescriptor',
        'vb/ServiceMetadataProviderDescriptor':
          'vb/extensions/dynamic/private/types/serviceMetadataProviderDescriptor',
        'vb/DynamicLayoutMetadataProviderDescriptor':
          'vb/extensions/dynamic/private/types/dynamicLayoutMetadataProviderDescriptor',
        'vb/ContainerMetadataProviderDescriptor':
          'vb/extensions/dynamic/private/types/containerMetadataProviderDescriptor',
        'vb/HeterogeneousMetadataProviderDescriptor':
          'vb/extensions/dynamic/private/types/heterogeneousMetadataProviderDescriptor',

        main: 'vb/main',
        vbIconFontCss: `css!${scriptRoot}resources/vb-icon-font`,

        // for backwards compatibility with the opt prefix
        opt: 'persist',
      },
    };

    return requireConfig;
  }

  /**
   * Detect if we have the chrome debugger extension installed.
   */
  function debuggerHandShake() {
    // only install the listener for chrome
    if (navigator.userAgent.toLowerCase().indexOf('chrome') > -1) {
      window.addEventListener('message', (event) => {
        const data = event.data;
        const type = data.type;

        if (type === 'vbDebuggerHandShake') {
          // If we get a hand shake message, invoke handShake method on the debugger to make
          // sure it's installed. Also import versions to initialize the version data since it needs to
          // communicated to the debugger during hand shake.
          requirejs(['vb/private/debug/applicationDebugStream', 'vb/versions'], (ApplicationDebugStream) => {
            ApplicationDebugStream.handShake();
          });
        } else if (type === 'vbDebuggerUninstalled') {
          console.log('Debugger extension uninstalled');
          VB_CONF.debuggerInstalled = false;
        }
      }, false);
    }
  }

  // It is possible that window.vbInitConfig is not defined and that requirejs has
  // already been configured, in that case, skip the configuration
  if (Object.keys(VB_CONF).length > 0) {
    // Call requirejs.config once with the complete configuration
    requirejs.config(buildRequireConfig(VB_CONF.IS_MOBILE));
  }

  function getServiceWorkerManagerClass(Utils) {
    let swmClass = 'vbsw/private/serviceWorkerManagerClass';
    // make sure window.vb is defined
    return Utils.getResource('vbc/private/pwa/pwaUtils')
      .then((pwaUtils) => {
        // Even if this is a PWA, don't enable Service Worker in DT preview
        if (!VB_CONF.IS_DT_MODE && pwaUtils.isWebPwaConfig(VB_CONF)) {
          swmClass = 'vb/private/pwa/pwaServiceWorkerManagerClass';
        }
        return Utils.getResource(swmClass);
      });
  }

  function installServiceWorkers(Utils, config, Configuration) {
    // skip install service worker if skipInstall is set to true
    if (VB_CONF.SERVICE_WORKER_CONFIG && VB_CONF.SERVICE_WORKER_CONFIG.skipInstall) {
      return Promise.resolve();
    }
    let swmInstance;
    return getServiceWorkerManagerClass(Utils)
      .then((SWManagerClass) => {
        swmInstance = new SWManagerClass();
        return Utils.getResource('vbsw/private/serviceWorkerManager');
      })
      .then((swm) => {
        swm.setInstance(swmInstance);
        return swm.getInstance().installServiceWorkers(scriptRoot, config, Configuration);
      })
      .then(() => swmInstance)
      .then((serviceWorkerManager) => serviceWorkerManager.installOfflineHandler(config));
  }

  /**
   * Require the library to communicate with DT when an application runs inside DT.
   * This needs to be done before other RT libraries in order for page preview to work in DT
   * @param  {function} initRt a callback to invoke Runtime initialization
   */
  function initRtDtEnv(initRt) {
    // Require the library to communicate with DT when an application runs inside DT.
    // This needs to be done before other library for page preview to work
    requirejs(['vb/private/rtEnvConfig'], (RtEnvConfig) => {
      const rtModule = RtEnvConfig && new RtEnvConfig().module;
      if (rtModule) {
        // This is when running in DT environment
        requirejs([rtModule], initRt);
      } else {
        initRt();
      }
    });
  }

  function init() {
    // detect chrome debugger extension
    debuggerHandShake();

    // Init RtDt environment first then continue with RT initalization
    initRtDtEnv(() => {
      requirejs(['promise', 'vb/private/constants', 'vb/private/polyfills'], (Promise, Constants) => {
        // first make sure Promise is polyfilled - this is taken from ojcore.js
        // this code executes before ojcore in included/required, so we need to make sure the es6-promise is loaded
        Promise['polyfill'](); // eslint-disable-line dot-notation

        if (VB_CONF.TEST_MODE === Constants.TestMode.WEBDRIVER) {
          // for security reason, we'll only enable loading of vb-test-utils.js if the application has
          // vbInitConfig.TEST_MODE set to webdriver
          requirejs.config({
            paths: {
              vbtu: `${scriptRoot}vb-test-utils`,
            },
            bundles: {
              vbtu: ['vbtu/testUtils'],
            },
          });
        }

        requirejs(['vb/private/utils', 'vb/private/configuration', 'vb/private/configLoader'],
          (Utils, Configuration, ConfigLoader) => {
            Configuration.init(configuration);

            if (VB_CONF.TEST_MODE === Constants.TestMode.ACTION_CHAIN) {
              // when running in action chain test mode, skip bootstrapping the application
              console.log('Starting VB in action chain test mode.');

              // send a message to the action chain tester to start running the test
              window.postMessage({
                type: 'vbActionChainTesterHandShake',
              }, window.location.origin);
            } else {
              // install service worker, then load application configuration
              ConfigLoader.init()
                .then((config) => installServiceWorkers(Utils, config, Configuration)
                  .then(() => Utils.getResource('vb/bootstrap'))
                  .then((bootstrap) => bootstrap()));
            }
          });
      });
    });
  }

  /**
   * Initialize the Cordova plugin used with the device Virtual Keyboard. We put a fix for the following issues:
   * 1. When the virtual keyboard is displayed the Webview needs to be shrunk otherwise the native view will introduce
   *    a scrollbar and scroll the top of the WebView off, which will scroll the fixed header off in the app.
   * 2. When the webview is shrunk, any input fields that are near the bottom need to be brought back into viewport.
   *    This is because the view port needs to be scrolled by the reduction in size of the webview. Our fix is in html
   *    and JS which is similar to the recommended native fix from Apple:
   *
   *    https://developer.apple.com/library/archive/documentation/StringsTextFonts/Conceptual/TextAndWebiPhoneOS/KeyboardManagement/KeyboardManagement.html#//apple_ref/doc/uid/TP40009542-CH5-SW7
   */
  function initVirtualKeyboard() {
    if (window.Keyboard) {
      // Use the keyboard plugin to shrink the webview
      window.Keyboard.shrinkView(true);

      const isInViewport = (elem) => {
        const bounding = elem.getBoundingClientRect();
        return (
          bounding.top >= 0
          && bounding.left >= 0
          && bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight)
          && bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
      };

      // setup a listener for 'keyboardDidShow' event to scroll element under keyboard into view
      window.addEventListener('keyboardDidShow', () => {
        const activeElem = document.activeElement;
        // window viewport is bigger than the screen then the viewport has scrollbars
        if (activeElem && (window.innerHeight < window.screen.height)) {
          // use timeout so that the viewport is sized and adjusted by native code first.
          // 100 ms is arbitrary low number to jump over native code in iOS. Android native code adjusts the viewport
          // correctly so the fix is only for iOS, i.e. it does not effect Android.
          window.setTimeout(() => {
            if (!isInViewport(activeElem)) {
              activeElem.scrollIntoView();
            }
          }, 100);
        }
      }, false);
    }
  }

  function initMobile() {
    init();

    // ensure window.open is set to the inAppBrowser's open API
    window.open = cordova.InAppBrowser.open;

    // Apply the JET platform marker class:
    document.body.classList.add(`oj-platform-${cordova.platformId}`);
    navigator.splashscreen.hide();

    // Initialize the virtualKeyboard
    initVirtualKeyboard();
  }

  /**
   * Return true if we are in the hidden iframe for refreshing the access token.
   *
   * @returns {boolean}
   */
  function inRefreshAuthTokenIframe() {
    try {
      return window.self !== window.top && window.frameElement.id === 'vb-refresh-access-token-iframe';
    } catch (e) {
      return false;
    }
  }

  /**
   * Post the access token which should be attached to the URL as a hash back to the parent window.
   */
  function refreshAuthToken() {
    window.parent.postMessage({
      method: 'vbRefreshAuthToken',
      args: [window.location.hash],
    }, window.location.origin);
  }

  if (VB_CONF.IS_MOBILE) {
    document.addEventListener('deviceready', initMobile, false);
  } else if (!inRefreshAuthTokenIframe()) {
    init();
  } else {
    refreshAuthToken();
  }
}());

define("generated/vb/main", function(){});

